var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Attachment } from '../../../molecules/Attachment';
import { Text } from '../../../atoms/Text/Text';
import { ToastContext } from '../../../../contexts/toast-context';
import { refreshFileDownloadUrl } from '../../../../features/AIChatWidget/services/uploadApi';
import { DOWNLOAD_REFRESH_BUFFER_MS, needsFreshDownloadUrl } from '../utils/presignedUrl';
import './FileDownloadRendererStyles.scss';
const baseClass = 'qmb-ui-file-download-renderer';
/**
 * Maps FileDownloadBlock file_type to Attachment component's expected type
 */
function mapFileTypeToAttachmentType(fileType) {
    switch (fileType) {
        case 'image':
            return 'image';
        case 'voice':
            return 'audio';
        case 'csv':
        case 'excel':
        case 'pdf':
        case 'document':
        case 'archive':
        case 'unknown':
        default:
            return 'document';
    }
}
/**
 * The backend that owns a block with no `download_source` on it.
 *
 * Only blocks persisted before IP-15871 lack one, and every download that
 * existed then came from the orchestrator's generated-file store.
 */
const LEGACY_DOWNLOAD_SOURCE = 'ai_generated_file';
/**
 * Opens a download URL in a new tab, rejecting unsafe URL schemes (javascript:,
 * data:, file:, blob:, etc.) to prevent XSS.
 */
function openDownloadUrl(url) {
    try {
        const parsedUrl = new URL(url);
        const safeProtocols = ['http:', 'https:'];
        if (safeProtocols.includes(parsedUrl.protocol)) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        // Silently reject unsafe schemes.
    }
    catch (_a) {
        // Invalid URL - do not open
    }
}
/**
 * Navigates a window opened earlier (during the click's user activation) to
 * the given URL, rejecting unsafe schemes the same way openDownloadUrl does.
 * Closes the window instead if the URL is missing or unsafe.
 */
function navigateOpenedWindow(target, url) {
    if (!target)
        return;
    try {
        const parsedUrl = new URL(url);
        const safeProtocols = ['http:', 'https:'];
        if (safeProtocols.includes(parsedUrl.protocol)) {
            target.location.href = url;
            return;
        }
    }
    catch (_a) {
        // Invalid URL - fall through to close
    }
    target.close();
}
export function FileDownloadRenderer({ block }) {
    var _a;
    const fileBlock = block;
    const { alertError } = useContext(ToastContext);
    // The block's URL/expiry are replaced locally once a refresh succeeds, so a
    // second click after a refresh re-checks the NEW expiry rather than the
    // stale one baked into the original block.
    const [downloadUrl, setDownloadUrl] = useState(fileBlock.download_url);
    const [expiresAt, setExpiresAt] = useState(fileBlock.download_url_expires_at);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // The handle a refresh is made with. `download_ref` is the field that carries
    // it on both paths — a Rails CSV export ships an EMPTY file_id by design, so
    // gating on file_id (as this did before IP-15871) permanently disabled the
    // button for every Rails export. file_id is only the fallback for blocks
    // persisted before download_ref existed, which would otherwise lose a
    // refresh they have today.
    const refreshRef = fileBlock.download_ref || fileBlock.file_id;
    const refreshSource = (_a = fileBlock.download_source) !== null && _a !== void 0 ? _a : LEGACY_DOWNLOAD_SOURCE;
    // A failed refresh deliberately does NOT latch. The common failure is a
    // transient network blip, and latching turns that into a card the user cannot
    // recover without reloading the page. A genuinely dead export just produces
    // the toast again, which is honest. The only unrecoverable state is having no
    // handle to refresh with at all.
    const canRefresh = Boolean(refreshRef);
    const needsRefresh = useMemo(() => needsFreshDownloadUrl(downloadUrl, expiresAt, DOWNLOAD_REFRESH_BUFFER_MS), [downloadUrl, expiresAt]);
    const handleDownload = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isRefreshing)
            return;
        // Re-check at click time rather than trusting the memo: the card may have
        // rendered minutes ago, and the buffer exists precisely to stop a link that
        // was fine at render from being handed over dead.
        if (!needsFreshDownloadUrl(downloadUrl, expiresAt, DOWNLOAD_REFRESH_BUFFER_MS)) {
            openDownloadUrl(downloadUrl);
            return;
        }
        if (!refreshRef) {
            alertError('This download link has expired. Please request a new download.');
            return;
        }
        // Open the target window SYNCHRONOUSLY, during the click's user
        // activation, then navigate it once the refresh resolves. Calling
        // window.open() after an await loses activation in popup-blocking
        // browsers, which would otherwise silently swallow a successful
        // refresh.
        const pending = window.open('', '_blank', 'noopener,noreferrer');
        setIsRefreshing(true);
        try {
            const refreshed = yield refreshFileDownloadUrl(refreshRef, refreshSource);
            setDownloadUrl(refreshed.download_url);
            setExpiresAt(refreshed.expires_at);
            navigateOpenedWindow(pending, refreshed.download_url);
        }
        catch (_a) {
            pending === null || pending === void 0 ? void 0 : pending.close();
            alertError('Could not refresh this download link. Please try again.');
        }
        finally {
            setIsRefreshing(false);
        }
    }), [isRefreshing, expiresAt, downloadUrl, refreshRef, refreshSource, alertError]);
    let label = 'Download';
    let icon = 'fa-light fa-download';
    if (isRefreshing) {
        label = 'Refreshing…';
        icon = 'fa-light fa-spinner-third';
    }
    else if (needsRefresh) {
        label = canRefresh ? 'Refresh & Download' : 'Link Expired';
        icon = canRefresh ? 'fa-light fa-rotate' : 'fa-light fa-clock';
    }
    return (React.createElement(AIGeneralBlockCard, { sections: [
            {
                label: fileBlock.file_name,
                content: (React.createElement("div", { className: baseClass },
                    React.createElement(Attachment, { id: fileBlock.block_id, name: fileBlock.file_name, type: mapFileTypeToAttachmentType(fileBlock.file_type), readOnly: true }),
                    React.createElement(Text, { variant: "body", className: `${baseClass}__size` }, fileBlock.file_size_display),
                    fileBlock.generation_context && (React.createElement(Text, { variant: "body", className: `${baseClass}__context` }, fileBlock.generation_context))))
            }
        ], footerGroups: [
            {
                actions: [
                    {
                        id: 'download',
                        label,
                        icon,
                        onClick: () => {
                            void handleDownload();
                        },
                        disabled: isRefreshing || (needsRefresh && !canRefresh)
                    }
                ]
            }
        ] }));
}
